import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useRef } from 'react';
import { useResolvedOnServer } from '~/context/common/DeferredResolutionContext';
import {
  useConfiguration,
  useEventCorrelationId
} from '~/context/common/StaticContext';
import { useUserId, useUserLocation } from '~/context/common/UserContext';
import DiscountContext from '~/context/vdp/DiscountContext';
import useSkipFirstFetchIfResolvedOnServer from '~/hooks/common/useSkipFirstFetchIfResolvedOnServer';
import fetchDiscounts from '~/services/vdp/fetchDiscounts';
import fetchDiscountsLegacy from '~/services/vdp/fetchDiscountsLegacy';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useGetDiscounts(isLoggedIn: boolean) {
  const {
    discounts: { setData, setError, setLoading },
    fetchDiscountsFlag
  } = useContext(DiscountContext);
  const { enableOECDiscountsApi } = useConfiguration();
  const { discounts: discountsResolvedOnServer } = useResolvedOnServer();
  const shouldSkipFetch = useSkipFirstFetchIfResolvedOnServer(
    discountsResolvedOnServer
  );
  const lastFetchFlag = useRef(fetchDiscountsFlag);
  const eventCorrelationId = useEventCorrelationId();
  const userId = useUserId();
  const { state: stateAbbreviation } = useUserLocation();

  useEffectWithController(
    controller => {
      if (!isLoggedIn) {
        setData([]);

        return;
      }

      const wasTriggered = fetchDiscountsFlag !== lastFetchFlag.current;
      lastFetchFlag.current = fetchDiscountsFlag;

      // A miss-triggered fetch always runs, even during the server-resolved skip window.
      if (shouldSkipFetch() && !wasTriggered) {
        return;
      }

      setLoading();

      const discounts = enableOECDiscountsApi
        ? fetchDiscounts(eventCorrelationId, controller.signal)
        : fetchDiscountsLegacy(stateAbbreviation, userId, controller);

      discounts
        .then(setData)
        .catch(
          generateErrorLogger(
            'Get discounts',
            {},
            { callback: setError, controller }
          )
        );
    },
    [
      enableOECDiscountsApi,
      eventCorrelationId,
      fetchDiscountsFlag,
      isLoggedIn,
      setData,
      setError,
      setLoading,
      shouldSkipFetch,
      stateAbbreviation,
      userId
    ]
  );
}
